import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Tooltip, Typography, useTheme, useMediaQuery } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { compareConsentOptions } from '../../utils/helper.utils';
import { useTranslation } from '../../hooks/useTranslation';

const CHIP_MAX = { mobile: 100, desktop: 160 };
const CONNECTOR = { mobile: 10, desktop: 20 };
const CHIPS_PER_ROW_MAX = 3;

const buildChipRows = (total, chipsPerRow) => {
	const rows = [];
	for (let i = 0; i < total; i += chipsPerRow) {
		rows.push(
			Array.from({ length: Math.min(chipsPerRow, total - i) }, (_, offset) => i + offset)
		);
	}
	return rows;
};

const getMaxFitInRow = (containerWidth, isMobile) => {
	const chipW = isMobile ? CHIP_MAX.mobile : CHIP_MAX.desktop;
	const connW = isMobile ? CONNECTOR.mobile : CONNECTOR.desktop;
	const unit = chipW + connW;
	return Math.max(1, Math.floor((containerWidth + connW) / unit));
};

const getLayoutChipsPerRow = (containerWidth, isMobile, totalChips) => {
	if (!containerWidth) {
		return Math.min(totalChips, CHIPS_PER_ROW_MAX);
	}

	const maxFit = getMaxFitInRow(containerWidth, isMobile);
	if (totalChips <= maxFit) {
		return totalChips;
	}

	return Math.min(maxFit, CHIPS_PER_ROW_MAX);
};

const getSlotIndex = (rowIdx, posInRow, chipsPerRow) =>
	rowIdx % 2 === 0 ? posInRow : chipsPerRow - 1 - posInRow;

const buildRowConnectorPath = (fromRect, toRect, containerRect) => {
	const x = (fromRect.left + fromRect.right) / 2 - containerRect.left;
	const startY = fromRect.bottom - containerRect.top;
	const endY = toRect.top - containerRect.top;
	return `M ${x} ${startY} V ${endY}`;
};

const ConsentStepIndicator = ({
	consents,
	activeConsent,
	acceptedConsents,
	deniedConsents,
	customDetails,
}) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const isDark = customDetails?.darkTheme === true || theme.palette.mode === 'dark';
	const primaryColor = customDetails?.primaryColor || theme.palette.primary.main;
	const successColor = customDetails?.successColor || theme.palette.success.main;
	const successDark = theme.palette.success.dark || '#2E7D32';
	const errorColor = customDetails?.errorColor || theme.palette.error.main;
	const futureTextColor = isDark ? 'rgba(255, 255, 255, 0.45)' : '#6C6C6C';
	const futureBorderColor = isDark ? 'rgba(255, 255, 255, 0.2)' : '#E0E0E0';
	const futureBgColor = isDark ? 'rgba(255, 255, 255, 0.05)' : '#F5F5F5';
	const getChipTint = (color, percent = 5) =>
		isDark
			? `color-mix(in srgb, ${color} ${Math.max(percent + 3, 4)}%, transparent)`
			: `color-mix(in srgb, ${color} ${percent}%, #ffffff)`;
	const chipBorderRadius = customDetails?.borderRadius || '8px';
	const containerRef = useRef(null);
	const breakpointKey = isMobile ? 'm' : 'd';
	const [measuredLayout, setMeasuredLayout] = useState({ key: breakpointKey, width: 0 });
	const [rowConnectors, setRowConnectors] = useState([]);
	const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });

	const chipMaxPx = isMobile ? CHIP_MAX.mobile : CHIP_MAX.desktop;
	const connectorPx = isMobile ? CONNECTOR.mobile : CONNECTOR.desktop;

	const effectiveWidth =
		measuredLayout.key === breakpointKey ? measuredLayout.width : 0;

	const chipsPerRow = useMemo(
		() => getLayoutChipsPerRow(effectiveWidth, isMobile, consents.length),
		[effectiveWidth, isMobile, consents.length]
	);

	const chipRows = useMemo(
		() => buildChipRows(consents.length, chipsPerRow),
		[consents.length, chipsPerRow]
	);

	const useSnakeLayout = chipRows.length > 1;
	const layoutKey = `${isMobile ? 'm' : 'd'}-${chipsPerRow}-${consents.length}`;

	useLayoutEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const width = container.clientWidth;
		setMeasuredLayout((prev) =>
			prev.key === breakpointKey && prev.width === width
				? prev
				: { key: breakpointKey, width }
		);
		setRowConnectors([]);
	}, [breakpointKey, consents.length]);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const measureWidth = () => {
			const width = container.clientWidth;
			if (!width) return;
			setMeasuredLayout((prev) =>
				prev.key === breakpointKey && prev.width === width
					? prev
					: { key: breakpointKey, width }
			);
		};

		const observer = new ResizeObserver(measureWidth);
		observer.observe(container);
		window.addEventListener('resize', measureWidth);

		return () => {
			observer.disconnect();
			window.removeEventListener('resize', measureWidth);
		};
	}, [breakpointKey]);

	useLayoutEffect(() => {
		const container = containerRef.current;
		if (!container || !effectiveWidth) {
			setRowConnectors([]);
			return;
		}

		const getChipEl = (idx) =>
			container.querySelector(`[data-step-chip][data-step-index="${idx}"]`);

		if (!useSnakeLayout) {
			setRowConnectors([]);
			setSvgSize({ width: container.offsetWidth, height: container.offsetHeight });
			return;
		}

		const containerRect = container.getBoundingClientRect();
		const connectors = [];

		for (let rowIdx = 0; rowIdx < chipRows.length - 1; rowIdx += 1) {
			const fromIdx = chipRows[rowIdx][chipRows[rowIdx].length - 1];
			const toIdx = chipRows[rowIdx + 1][0];
			const fromEl = getChipEl(fromIdx);
			const toEl = getChipEl(toIdx);
			if (!fromEl || !toEl) continue;

			connectors.push({
				fromIdx,
				path: buildRowConnectorPath(
					fromEl.getBoundingClientRect(),
					toEl.getBoundingClientRect(),
					containerRect
				),
			});
		}

		setRowConnectors(connectors);
		setSvgSize({
			width: container.offsetWidth,
			height: container.offsetHeight,
		});
	}, [layoutKey, chipRows, consents.length, useSnakeLayout, effectiveWidth]);

	const getConsentStatus = (consentHandle, index) => {
		if (acceptedConsents.includes(consentHandle)) return 'accepted';
		if (deniedConsents.includes(consentHandle)) return 'denied';
		if (index === activeConsent) return 'active';
		if (index < activeConsent) return 'past';
		return 'future';
	};

	const getPurposeText = (consent) =>
		compareConsentOptions(consent, global.consentOptions, t);

	const getLineColor = (status) => {
		if (status === 'accepted') return successDark;
		if (status === 'denied') return errorColor;
		if (status === 'active' || status === 'past') return primaryColor;
		return isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.25)';
	};

	const getChipStyles = (status) => {
		const base = {
			display: 'inline-flex',
			alignItems: 'center',
			gap: isMobile ? '0.3rem' : '0.375rem',
			borderRadius: chipBorderRadius,
			height: isMobile ? '28px' : '32px',
			px: isMobile ? '0.4rem' : '0.75rem',
			width: `${chipMaxPx}px`,
			maxWidth: `${chipMaxPx}px`,
			minWidth: 0,
			boxSizing: 'border-box',
			border: '1px solid',
			overflow: 'hidden',
		};

		switch (status) {
			case 'active':
				return {
					...base,
					borderColor: primaryColor,
					backgroundColor: getChipTint(primaryColor),
				};
			case 'accepted':
				return {
					...base,
					borderColor: futureBorderColor,
					backgroundColor: getChipTint(successDark, 2),
				};
			case 'denied':
				return {
					...base,
					borderColor: futureBorderColor,
					backgroundColor: getChipTint(errorColor, 2),
				};
			case 'past':
				return {
					...base,
					borderColor: futureBorderColor,
					backgroundColor: getChipTint(successDark, 2),
				};
			default:
				return {
					...base,
					borderColor: futureBorderColor,
					backgroundColor: futureBgColor,
				};
		}
	};

	const getTextStyles = (status) => {
		let color = futureTextColor;
		if (status === 'active') {
			color = isDark ? '#FFFFFF' : primaryColor;
		} else if (status === 'accepted' || status === 'past') {
			color = isDark ? successColor : successDark;
		} else if (status === 'denied') {
			color = errorColor;
		}

		return {
			fontSize: isMobile ? '0.65rem' : '0.875rem',
			fontWeight: 600,
			color,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			lineHeight: 1.2,
			minWidth: 0,
			flex: 1,
		};
	};

	const getBadgeStyles = (status) => {
		const badgeSize = isMobile ? 18 : 20;

		if (status === 'accepted') {
			return {
				width: badgeSize,
				height: badgeSize,
				borderRadius: '50%',
				backgroundColor: successDark,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				flexShrink: 0,
			};
		}

		if (status === 'denied') {
			return {
				width: badgeSize,
				height: badgeSize,
				borderRadius: '50%',
				backgroundColor: errorColor,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				flexShrink: 0,
			};
		}

		return {
			width: badgeSize,
			height: badgeSize,
			borderRadius: '50%',
			backgroundColor: '#FFFFFF',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			flexShrink: 0,
			fontSize: isMobile ? '0.65rem' : '0.75rem',
			fontWeight: 600,
			color:
				status === 'active'
					? primaryColor
					: futureTextColor,
		};
	};

	const renderChip = (consent, idx) => {
		const status = getConsentStatus(consent.ConsentHandle, idx);
		const purposeText = getPurposeText(consent);

		return (
			<Tooltip
				title={purposeText}
				arrow
				placement="top"
				enterTouchDelay={0}
				leaveTouchDelay={3000}
				PopperProps={{
					sx: {
						'& .MuiTooltip-tooltip': {
							fontSize: isMobile ? '0.7rem' : '0.875rem',
							maxWidth: isMobile ? '220px' : '300px',
							padding: isMobile ? '0.4rem 0.6rem' : '0.5rem 1rem',
						},
					},
				}}
			>
				<Box
					data-step-chip
					data-step-index={idx}
					sx={{
						...getChipStyles(status),
						position: 'relative',
						zIndex: 1,
					}}
				>
					<Box sx={getBadgeStyles(status)}>
						{status === 'accepted' ? (
							<CheckIcon
								sx={{
									fontSize: isMobile ? '0.75rem' : '0.85rem',
									color: '#FFFFFF',
								}}
							/>
						) : status === 'denied' ? (
							<CloseIcon
								sx={{
									fontSize: isMobile ? '0.75rem' : '0.85rem',
									color: '#FFFFFF',
								}}
							/>
						) : (
							idx + 1
						)}
					</Box>
					<Typography component="span" sx={getTextStyles(status)}>
						{purposeText}
					</Typography>
				</Box>
			</Tooltip>
		);
	};

	const renderConnector = (idx) => {
		const status = getConsentStatus(consents[idx].ConsentHandle, idx);
		return (
			<Box
				sx={{
					width: `${connectorPx}px`,
					height: '2px',
					flexShrink: 0,
					backgroundColor: getLineColor(status),
					position: 'relative',
					zIndex: 1,
				}}
			/>
		);
	};

	const buildRowSlots = (rowIndices, rowIdx) => {
		const slots = Array(chipsPerRow).fill(null);
		rowIndices.forEach((idx, posInRow) => {
			slots[getSlotIndex(rowIdx, posInRow, chipsPerRow)] = idx;
		});
		return slots;
	};

	const renderPackedRow = (rowIndices) => (
		<Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0, flexWrap: 'nowrap' }}>
			{rowIndices.map((idx, posInRow) => (
				<React.Fragment key={consents[idx].ConsentHandle}>
					{renderChip(consents[idx], idx)}
					{posInRow < rowIndices.length - 1 && renderConnector(idx)}
				</React.Fragment>
			))}
		</Box>
	);

	const renderSnakeRow = (rowIndices, rowIdx) => {
		const slots = buildRowSlots(rowIndices, rowIdx);

		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					width: '100%',
					minWidth: 0,
				}}
			>
				{slots.map((chipIdx, slot) => (
					<React.Fragment key={`${rowIdx}-slot-${slot}`}>
						<Box
							sx={{
								flex: 1,
								minWidth: 0,
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
							}}
						>
							{chipIdx !== null && renderChip(consents[chipIdx], chipIdx)}
						</Box>
						{slot < chipsPerRow - 1 &&
							(() => {
								const leftIdx = slots[slot];
								const rightIdx = slots[slot + 1];
								const isFlowConnector =
									leftIdx !== null &&
									rightIdx !== null &&
									(rowIdx % 2 === 0
										? rightIdx === leftIdx + 1
										: leftIdx === rightIdx + 1);

								if (isFlowConnector) {
									const connectorIdx = rowIdx % 2 === 0 ? leftIdx : rightIdx;
									return renderConnector(connectorIdx);
								}

								return (
									<Box sx={{ width: `${connectorPx}px`, flexShrink: 0 }} />
								);
							})()}
					</React.Fragment>
				))}
			</Box>
		);
	};

	return (
		<Box
			ref={containerRef}
			sx={{
				position: 'relative',
				display: 'flex',
				flexDirection: 'column',
				mb: '1.5rem',
				pb: '0.5rem',
				width: '100%',
				minWidth: 0,
				overflow: 'hidden',
				gap: isMobile ? '0.5rem' : '0.4rem',
				boxSizing: 'border-box',
				px: { xs: '1rem', md: '1rem' },
			}}
		>
			{svgSize.width > 0 && rowConnectors.length > 0 && (
				<Box
					component="svg"
					width={svgSize.width}
					height={svgSize.height}
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						pointerEvents: 'none',
						overflow: 'visible',
						zIndex: 0,
					}}
				>
					{rowConnectors.map(({ fromIdx, path }) => {
						const status = getConsentStatus(
							consents[fromIdx].ConsentHandle,
							fromIdx
						);
						return (
							<path
								key={`row-connector-${fromIdx}`}
								d={path}
								fill="none"
								stroke={getLineColor(status)}
								strokeWidth={2}
								strokeLinecap="round"
							/>
						);
					})}
				</Box>
			)}

			<Box
				key={layoutKey}
				sx={{
					display: 'flex',
					flexDirection: 'column',
					width: '100%',
					minWidth: 0,
				}}
			>
				{chipRows.map((rowIndices, rowIdx) => (
					<Box key={`row-${rowIdx}`} sx={{ mb: rowIdx < chipRows.length - 1 ? (isMobile ? '0.5rem' : '0.4rem') : 0 }}>
						{!useSnakeLayout
							? renderPackedRow(rowIndices)
							: renderSnakeRow(rowIndices, rowIdx)}
					</Box>
				))}
			</Box>
		</Box>
	);
};

export default ConsentStepIndicator;
